import { service } from '@ember/service'
import Component from '@glimmer/component'
import { t, type IntlService } from 'ember-intl'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { SelectBox } from 'teacher-ui-client/components/select-box'
import type { SelectOption } from '@blakeelearning/ember-select'
import { fullGradeName } from 'teacher-ui-client/helpers/full-grade-name'

export type LessonStandard = {
  codes: string[]
  concepts: string[]
  content_level: number
  lesson: number
  name: string
}

export interface LessonSelectionData {
  lesson_standards: LessonStandard[]
}

interface Signature {
  Args: {
    lessonData: LessonStandard[]
    setLesson: (lesson: number) => void
    selectedLesson: number
    isFastPhonics?: boolean
    standardsSet?: string
  }
  Element: Element
}

export class LessonSelectionPanel extends Component<Signature> {
  @service intl!: IntlService

  get selectedLesson(): string {
    return String(this.args.selectedLesson)
  }

  get selectedLessonData(): LessonStandard | undefined {
    return this.args.lessonData.find((lessonItem) => String(lessonItem.lesson) === this.selectedLesson)
  }

  lessonName = (lesson: LessonStandard): string => {
    if (this.args.isFastPhonics) {
      return this.intl.t('reporting.lessonPerformance.lessonSelectionNameFp', {
        lesson: lesson.lesson,
        name: lesson.name,
      })
    }
    return this.intl.t('reporting.lessonPerformance.lessonSelectionName', {
      lesson: lesson.lesson,
      name: lesson.name,
    })
  }

  get codes(): string {
    return this.selectedLessonData?.codes.join(', ') ?? ''
  }

  get lessonOptions(): SelectOption[] {
    return (
      this.args.lessonData?.map((lessonItem) => ({
        value: String(lessonItem.lesson),
        label: this.lessonName(lessonItem),
      })) ?? []
    )
  }

  onSelectLesson = ({ value }: SelectOption) => {
    this.args.setLesson(Number(value))
  }

  <template>
    <ContentTile data-test-lesson-selection-panel class="p-6" ...attributes>
      <div class="grid grid-cols-7 gap-x-16" data-test-stat-box-container>
        <div class="col-span-2 space-y-8">
          <SelectBox
            data-test-lesson-selection-select-box
            @value={{this.selectedLesson}}
            @options={{this.lessonOptions}}
            @onSelect={{this.onSelectLesson}}
            @matchTriggerWidth={{true}}
          />
          <div data-test-lesson-selection-content-level><span class="font-semibold">{{t
                "reporting.lessonPerformance.selection.contentLevel"
              }}</span>
            {{fullGradeName this.selectedLessonData.content_level}}</div>
        </div>
        <div class="col-span-3 space-y-2">
          <div data-test-lesson-selection-lesson-focus class="font-semibold">{{if
              @isFastPhonics
              (t "reporting.lessonPerformance.selection.lessonFocusFp")
              (t "reporting.lessonPerformance.selection.lessonFocus")
            }}</div>
          {{#each this.selectedLessonData.concepts as |concept conceptIndex|}}
            <p data-test-lesson-selection-concept={{conceptIndex}}>{{concept}}</p>
          {{/each}}
        </div>
        <div class="col-span-2">
          <div class="font-semibold">{{t "reporting.lessonPerformance.selection.setStandards"}}</div>
          <p data-test-lesson-selection-standards-set>{{@standardsSet}}</p>
          <div class="mt-8 font-semibold">{{t "reporting.lessonPerformance.selection.standards"}}</div>
          <p data-test-lesson-selection-codes>{{this.codes}}</p>
        </div>
      </div>
    </ContentTile>
  </template>
}
